// ** React Imports
import React, { useEffect } from 'react'

// ** Next imports
import { useRouter } from 'next/router'

// ** MUI Imports
import { Typography } from '@mui/material'
import { Grid } from '@mui/material'
import { SwitchableTabType } from 'src/lib/types/random'
import Permission from 'src/services/PermissionService'
import { classNames, createDeepCopy } from 'src/lib/utils/helpers'

interface FormTileProps {
  children: JSX.Element
  tabList: SwitchableTabType[] | undefined
  setTabList: (
    tabList: SwitchableTabType[]
  ) => void | React.Dispatch<React.SetStateAction<SwitchableTabType[] | undefined>>
  customClass?: any
  isFragmentationEnabled?: boolean
  isUnsaved?: boolean
  setDefaultConfig?: () => void
  tabSwitcherClassName?: string
}

const TabSwitcher: React.FC<FormTileProps> = ({
  children,
  tabList,
  setTabList,
  customClass,
  isFragmentationEnabled,
  isUnsaved,
  setDefaultConfig,
  tabSwitcherClassName
}) => {
  const router = useRouter()

  useEffect(() => {
    if (tabList && isFragmentationEnabled) {
      for (let i = 0; i < tabList?.length; i++) {
        if (tabList[i]?.isActive === true) {
          const newHash = `${tabList[i]?.name?.toLowerCase()}`
          // Only update if the hash is different
          if (router.asPath.split('#')[1] !== newHash) {
            // Directly modify the URL without using router
            window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}#${newHash}`)
          }
        }
      }
    }
  }, [tabList, router.asPath, isFragmentationEnabled])

  const activateTab = (index: number) => {
    if (tabList) {
      const modifiedTabList = createDeepCopy<SwitchableTabType[]>(tabList) as SwitchableTabType[]
      for (let i = 0; i < modifiedTabList.length; i++) {
        modifiedTabList[i].isActive = false
      }
      modifiedTabList[index].isActive = true
      setTabList([...modifiedTabList])
    }
  }

  const setActiveTab = (index: number) => {
    if (!Boolean(isUnsaved)) {
      activateTab(index)
    } else {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        return
      } else {
        if (setDefaultConfig) setDefaultConfig()
        activateTab(index)
      }
    }
  }
  return (
    <Grid
      container
      gap={4}
      style={{
        width: '100%',
        overflow: 'visible',
        position: 'relative'
      }}
      className={`${customClass}`}
    >
      <Grid
        className={classNames(
          'flex w-full gap-4 align-center border-gray-light -ml-6 pl-6 ',
          tabSwitcherClassName ?? ''
        )}
      >
        {tabList?.map((tab: SwitchableTabType, index: number) => {
          const tabEl = () => (
            <>
              <Typography
                id={tab.id ?? `TAB-${tab.name.trim()}`}
                key={tab?.name}
                sx={{
                  fontSize: 14,
                  fontWeight: tab?.isActive ? 600 : 400,
                  cursor: 'pointer',
                  marginBottom: '-2px',
                  borderBottom: `2px solid  ${tab?.isActive ? '#6E6E6E' : '#6E6E6E00'}`,
                  zIndex: 20,
                  overflow: 'visible'
                }}
                variant={'subtitle1'}
                color={tab?.isActive ? 'text.primary' : 'text.secondary'}
                onClick={() => setActiveTab(index)}
              >
                {tab?.label}
              </Typography>
            </>
          )

          if (Boolean(tab.permissionId)) {
            return (
              <>
                <Permission permissionId={tab.permissionId} hideElement={tab.hideElement}>
                  {tabEl()}
                </Permission>
              </>
            )
          }
          return <>{tabEl()}</>
        })}
        <div className='w-full z-10 top-[25px] left-6 absolute border-gray-light border-b-2' />
      </Grid>
      {children}
    </Grid>
  )
}

export default TabSwitcher
